import { useMemo, useState } from 'react';
import { useFlashcardsInDeck } from '../../../hooks/useFlashcards';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  open: boolean;
  deckId: string;
  deckName: string;
  onCancel: () => void;
  /** Called with the chosen chapter label, or null for "all chapters". */
  onStart: (chapterFilter: string | null) => void;
}

/**
 * Pre-study chooser — asks whether to study the whole deck or just one chapter.
 * Skips rendering entirely when the deck has ≤ 1 chapter (caller handles that).
 */
export default function StudyScopeDialog({
  open,
  deckId,
  deckName,
  onCancel,
  onStart,
}: Props) {
  const { t } = useTranslation();
  const { flashcards } = useFlashcardsInDeck(open ? deckId : null);

  // Tally cards per chapter. `locationLabel` is set to the chapter title when
  // a card is picked from the library; hand-typed cards may have it empty
  // (those fall into an "Unspecified" bucket).
  const chapterCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of flashcards) {
      const key = (c.locationLabel || '').trim() || 'Unspecified';
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [flashcards]);

  const [selected, setSelected] = useState<string | null>(null); // null = all

  if (!open) return null;

  const total = flashcards.length;

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="study-scope-title"
    >
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 shadow-xl">
        <h3 id="study-scope-title" className="text-lg font-semibold mb-1">
          {t('App.Reader.Flashcard.StudyScope.Title', { deckName })}
        </h3>
        <p className="text-xs text-neutral-500 mb-4">
          {t('App.Reader.Flashcard.StudyScope.Description')}
        </p>

        <fieldset className="space-y-1 mb-4 max-h-64 overflow-y-auto">
          <legend className="sr-only">{t('App.Reader.Flashcard.StudyScope.Legend')}</legend>

          <label className="flex items-center gap-2 py-2 px-2 min-h-[44px] rounded hover:bg-neutral-50 cursor-pointer">
            <input
              type="radio"
              name="scope"
              checked={selected === null}
              onChange={() => setSelected(null)}
              className="w-5 h-5"
            />
            <span className="flex-1 text-sm">{t('App.Reader.Flashcard.StudyScope.AllChapters')}</span>
            <span className="text-xs text-neutral-500">{total}</span>
          </label>

          {chapterCounts.map(([label, count]) => (
            <label
              key={label}
              className="flex items-center gap-2 py-2 px-2 min-h-[44px] rounded hover:bg-neutral-50 cursor-pointer"
            >
              <input
                type="radio"
                name="scope"
                checked={selected === label}
                onChange={() => setSelected(label)}
                className="w-5 h-5"
              />
              <span className="flex-1 text-sm truncate">{label}</span>
              <span className="text-xs text-neutral-500">{count}</span>
            </label>
          ))}
        </fieldset>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="min-h-[44px] px-4 py-2 rounded border border-neutral-300 text-sm"
          >
            {t('App.Reader.Flashcard.StudyScope.CancelButton')}
          </button>
          <button
            onClick={() => onStart(selected)}
            disabled={total === 0}
            className="min-h-[44px] px-4 py-2 rounded bg-primary-600 text-white text-sm disabled:opacity-50"
          >
            {t('App.Reader.Flashcard.StudyScope.StartButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
